/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { ClinicProfileSettingsResponse } from "../../../../models/clinic/profile/ClinicProfileSettingsResponse";
import { useFieldArray, useForm } from "react-hook-form";
import { strings } from "../../../../common/Strings/Strings";
import ClinicProfileApi from "../../../../api/ClinicProfileApi";
import { canManageClinic, getGeneralError } from "../../../../util/helperFunctions";
import AlertBox, { AlertType } from "../../../../components/AlertBox";
import Button from "../../../../components/Button";
import { CustomSectionsRequest } from "../../../../models/clinic/profile/CustomSectionsRequest";
import Field from "../../../../components/ReactHookFormFields/General/Field";
import { Trash } from "../../../../common/Icons/Trash";
import Textarea from "../../../../components/ReactHookFormFields/General/Textarea";
import { PlusIcon } from "../../../../common/Icons/PlusIcon";
import { ClinicResponse } from "../../../../models/clinic/ClinicResponse";

interface Props {
  clinic: ClinicResponse;
  settings: ClinicProfileSettingsResponse;
  setSettings: (settings: ClinicProfileSettingsResponse) => void;
}

const CustomSections: React.FC<Props> = (props: Props) => {
  const { clinic, settings, setSettings } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<CustomSectionsRequest>({
    mode: "onSubmit",
    defaultValues: settings,
  });

  const customSectionsArray = useFieldArray({ control, name: "customSections" });

  useEffect(() => {
    reset(settings);
  }, [settings]);

  const saveSettings = async (request: CustomSectionsRequest) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const response = await ClinicProfileApi.updateCustomSections(clinic.id, request);
      setSettings(response.data);
      setSuccess(true);
    } catch (e) {
      setError(await getGeneralError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(saveSettings)}>
      <div className="tw-card divide-y divide-gray-200 dark:divide-gray-700">
        <div className="p-4 flex items-center">
          <p className="uppercase">{strings.clinicProfileCustomSections}</p>
        </div>
        <div className="p-4 space-y-4">
          <Button
            variant="link"
            onClick={() => customSectionsArray.append({ title: "", content: "" })}
            hidden={!canManageClinic(clinic)}
          >
            <div className="flex">
              <PlusIcon /> <span>{strings.clinicProfileCustomSectionAddNew}</span>
            </div>
          </Button>
          {customSectionsArray.fields.map((item, index) => (
            <div className="tw-card p-4 space-y-4">
              <div className="flex space-x-4 items-center">
                <Field
                  register={register}
                  name={`customSections.${index}.title`}
                  placeholder={strings.clinicProfileCustomSectionTitle}
                  className="flex-1"
                  error={errors.customSections && errors.customSections[index]?.title}
                  required
                  readOnly={!canManageClinic(clinic)}
                />
                <Button
                  variant="icon"
                  onClick={() => customSectionsArray.remove(index)}
                  hidden={!canManageClinic(clinic)}
                >
                  <Trash />
                </Button>
              </div>
              <Textarea
                label={strings.clinicProfileCustomSectionContent}
                name={`customSections.${index}.content`}
                register={register}
                minRows={5}
                error={errors.customSections && errors.customSections[index]?.content}
                maxLength={10000}
                required
                readOnly={!canManageClinic(clinic)}
              />
            </div>
          ))}
          <AlertBox message={error} />
          <AlertBox closeAble={false} hidden={!success} message={strings.savedSuccessfully} type={AlertType.SUCCESS} />
          <div className="text-right">
            <Button
              disabled={loading || !isDirty}
              loading={loading}
              type="submit"
              fullWidth={false}
              hidden={!canManageClinic(clinic)}
            >
              {strings.save}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default CustomSections;
